// ========================================
// RECEIPT CALCULATION SERVICE
// Service to calculate proper receipt totals and subtotals
// ========================================

import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';
import { resolveSaleReceiptDate } from '../utils/saleReceiptDateTime';
import { buildThermalReceiptHtml, getThermalReceiptBuildOptions } from './thermalReceipt';
import { buildClientA5ReceiptHtml } from './clientA5ReceiptHtml';
import { readActiveCurrencyType, toDisplayAmount } from '../utils/posCurrencyUtils';
import { assertCanPrintReceipt } from '../utils/workerPermissions';
import { printSilentFast } from '../utils/printerUtils';
import { PRINT_ROLE } from '../domain/print';
import {
  getClientReceiptPaperFormat,
  getPreferredThermalClientPaperFormat,
  isClientReceiptA5Format,
  normalizeClientReceiptPaperFormat,
  resolveClientReceiptSilentPrintPlan,
  clientPaperFormatToThermalWidth
} from '../utils/clientReceiptPaperFormat';
import {
  getSaleLineActiveAmount,
  getSaleLineActiveQuantity,
  getSaleLineUnitPrice
} from '../domain/sales/saleLineMath';

class ReceiptCalculationService {
  constructor() {
    this.defaultTaxRate = 0; // No tax by default - only apply if specified in sale data
  }

  /**
   * Calculate line totals for each product.
   * Uses same qty/price SSOT as void/revenue: active qty + pack-aware amount.
   */
  calculateLineTotals(products) {
    if (!products || !Array.isArray(products)) {
      return [];
    }

    return products.map((product) => {
      const quantity = getSaleLineActiveQuantity({
        ...product,
        quantity: product.quantity ?? product.qty ?? 0
      });
      const price = getSaleLineUnitPrice(product);
      const lineTotal = getSaleLineActiveAmount({
        ...product,
        quantity: product.quantity ?? product.qty ?? 0
      });

      return {
        ...product,
        name: product.name || product.productName || '',
        quantity,
        price,
        // Void already baked into quantity — clear so re-entry (totals) is idempotent.
        voidedQuantity: 0,
        lineTotal: Math.round(lineTotal * 100) / 100
      };
    });
  }

  /**
   * Calculate receipt totals
   */
  calculateReceiptTotals(products, discount = 0, taxRate = null) {
    const lineTotals = this.calculateLineTotals(products);
    
    // Calculate subtotal
    const subtotal = lineTotals.reduce((sum, item) => sum + item.lineTotal, 0);
    
    // Calculate discount amount
    const discountAmount = parseFloat(discount) || 0;
    const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
    
    // Calculate tax only if taxRate is provided, otherwise use 0
    const effectiveTaxRate = taxRate !== null ? parseFloat(taxRate) : this.defaultTaxRate;
    const tax = subtotalAfterDiscount * effectiveTaxRate;
    
    // Calculate total
    const total = subtotalAfterDiscount + tax;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount: discountAmount,
      subtotalAfterDiscount: Math.round(subtotalAfterDiscount * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(total * 100) / 100,
      lineTotals
    };
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount, currency = null) {
    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }

    const numericAmount = parseFloat(amount);
    const mroAmount = isNaN(numericAmount) ? 0 : numericAmount;
    const targetCurrency = currency || readActiveCurrencyType();
    const displayAmount = toDisplayAmount(mroAmount, targetCurrency);

    return `${displayAmount.toFixed(2)} ${targetCurrency}`;
  }

  /**
   * Generate standard receipt data
   */
  generateReceiptData(saleData) {
    // Handle different data structures
    let products = [];
    let discount = 0;
    
    if (saleData.products && Array.isArray(saleData.products)) {
      products = saleData.products;
    } else if (saleData.items && Array.isArray(saleData.items)) {
      products = saleData.items;
    } else if (saleData.lineItems && Array.isArray(saleData.lineItems)) {
      products = saleData.lineItems;
    } else if (saleData.productId) {
      // Single product sale
      products = [{
        productId: saleData.productId,
        name: saleData.productName || saleData.name,
        quantity: saleData.quantity || saleData.qty,
        price: getSaleLineUnitPrice(saleData),
        sellingUnitPrice: getSaleLineUnitPrice(saleData),
        sellingPrice: saleData.sellingPrice,
        total: saleData.total || getSaleLineUnitPrice(saleData) * (saleData.quantity || saleData.qty || 0)
      }];
    }

    // Handle discount
    discount = parseFloat(saleData.discount || saleData.discountAmount || 0) || 0;
    
    // Handle tax rate - only apply if explicitly provided in sale data
    const taxRate = saleData.taxRate !== undefined ? parseFloat(saleData.taxRate) : null;

    const calculations = this.calculateReceiptTotals(products, discount, taxRate);
    const saleDate = resolveSaleReceiptDate(saleData, {
      useLiveTime: saleData?.status === 'pending'
    });

    return {
      orderId: saleData.orderId || saleData.previewOrderId || saleData.id || null,
      date: saleDate,
      workerName: saleData.workerName || saleData.sellerName || 'Vendeur',
      customerName: saleData.customerName || saleData.customer?.name || '',
      customerPhone: saleData.customerPhone || saleData.customer?.phone || '',
      location: saleData.location || saleData.stockLocation || 'Principal',
      products: calculations.lineTotals,
      subtotal: calculations.subtotal,
      discount: calculations.discount,
      subtotalAfterDiscount: calculations.subtotalAfterDiscount,
      tax: calculations.tax,
      total: calculations.total,
      paymentMethod: saleData.paymentMethod || 'cash',
      cashAmount: parseFloat(saleData.cashAmount || saleData.amountPaid || 0) || 0,
      change: parseFloat(saleData.change || saleData.changeAmount || 0) || 0,
      electronicPaymentMethod: saleData.electronicPaymentMethod || saleData.paymentType || '',
      notes: saleData.notes || saleData.comments || ''
    };
  }

  /**
   * Validate receipt data
   */
  validateReceiptData(receiptData) {
    const errors = [];

    if (!receiptData.products || receiptData.products.length === 0) {
      errors.push('No products in receipt');
    }

    if (receiptData.total <= 0) {
      errors.push('Total must be greater than 0');
    }

    if (receiptData.paymentMethod === 'cash' && receiptData.cashAmount < receiptData.total) {
      errors.push('Cash amount is less than total');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Normalize modal payload (nested businessInfo/saleData) into flat receipt shape.
   */
  _normalizePrintInput(rawData) {
    if (!rawData || typeof rawData !== 'object') {
      throw new Error('Invalid receipt data');
    }

    const saleData = rawData.saleData || rawData;
    const businessInfo = rawData.businessInfo || saleData.businessInfo || {};

    const merged = {
      ...saleData,
      businessInfo,
      orderId: rawData.orderId || saleData.orderId || saleData.id,
      date: rawData.date || saleData.date || saleData.createdAt,
      workerName: rawData.workerName || saleData.workerName,
      customerName: rawData.customerName || saleData.customerName,
      customerPhone: rawData.customerPhone || saleData.customerPhone,
      location: rawData.location || saleData.location,
      products: rawData.products || saleData.products || [],
      subtotal: rawData.subtotal ?? saleData.subtotal,
      discount: rawData.discount ?? saleData.discount,
      tax: rawData.tax ?? saleData.tax,
      total: rawData.total ?? saleData.total ?? saleData.totalAmount,
      paymentMethod: rawData.paymentMethod || saleData.paymentMethod,
      cashAmount: rawData.cashAmount ?? saleData.cashAmount,
      change: rawData.change ?? saleData.change,
      electronicPaymentMethod:
        rawData.electronicPaymentMethod || saleData.electronicPaymentMethod,
      notes: rawData.notes || saleData.notes
    };

    return this.generateReceiptData(merged);
  }

  _buildThermalPrintOptions(receiptData, options = {}) {
    const receiptType = options.forMerchant === true || options.type === 'merchant' ? 'merchant' : 'client';
    return getThermalReceiptBuildOptions(receiptType, options.language || 'fr', options);
  }

  /**
   * Build supermarket-style thermal receipt HTML (58 mm / 80 mm)
   * or A5 portrait document when paperFormat === 'A5'.
   */
  buildClientReceiptHTML(receiptData, options = {}) {
    const paperFormat = normalizeClientReceiptPaperFormat(
      options.paperFormat ?? getClientReceiptPaperFormat()
    );
    const language = options.language || options.lang || 'fr';
    const payload = { ...receiptData, businessInfo: receiptData.businessInfo || {} };
    if (isClientReceiptA5Format(paperFormat)) {
      return buildClientA5ReceiptHtml(payload, { language });
    }
    const paperWidth =
      options.paperWidth || clientPaperFormatToThermalWidth(paperFormat);
    return buildThermalReceiptHtml(
      payload,
      this._buildThermalPrintOptions(receiptData, {
        ...options,
        forMerchant: false,
        paperWidth,
        language
      })
    );
  }

  buildMerchantReceiptHTML(receiptData, options = {}) {
    return buildThermalReceiptHtml(
      { ...receiptData, businessInfo: receiptData.businessInfo || {} },
      this._buildThermalPrintOptions(receiptData, {
        ...options,
        forMerchant: true,
        paperWidth:
          options.paperWidth ||
          clientPaperFormatToThermalWidth(getPreferredThermalClientPaperFormat())
      })
    );
  }

  buildReceiptHTML(receiptData, options = {}) {
    return this.buildClientReceiptHTML(receiptData, options);
  }

  /**
   * Print receipt — always silent + fast to saved/default printer (Electron).
   * Browser fallback only when Electron IPC is unavailable.
   */
  async printReceipt(rawData, options = {}) {
    if (options.currentRole) {
      assertCanPrintReceipt(
        options.currentRole,
        options.language || options.lang || 'fr'
      );
    }

    const receiptType = options.type || rawData.receiptType || 'client';
    const receiptData = this._normalizePrintInput(rawData);
    const validation = this.validateReceiptData(receiptData);
    if (!validation.isValid) {
      throw new Error(validation.errors.join('; '));
    }

    const printPayload = {
      ...receiptData,
      merchantReceipt: rawData.merchantReceipt || rawData.saleData?.merchantReceipt,
      businessInfo: rawData.businessInfo || rawData.saleData?.businessInfo
    };

    const language = options.language || options.lang || 'fr';
    const paperFormat =
      receiptType === 'merchant'
        ? getPreferredThermalClientPaperFormat()
        : normalizeClientReceiptPaperFormat(
            options.paperFormat ?? getClientReceiptPaperFormat()
          );
    const plan = resolveClientReceiptSilentPrintPlan(paperFormat, { language });
    const paperWidth = options.paperWidth || plan.paperWidth;

    const html =
      receiptType === 'merchant'
        ? this.buildMerchantReceiptHTML(printPayload, {
            ...options,
            language,
            paperWidth
          })
        : this.buildClientReceiptHTML(printPayload, {
            ...options,
            paperFormat: plan.paperFormat,
            paperWidth,
            language
          });

    if (typeof window === 'undefined' || !window.electronAPI) {
      const { printHtmlInBrowser } = await import('../utils/printHtmlDocument');
      const windowFeatures = plan.isA5
        ? 'width=560,height=800'
        : plan.pageWidthMm <= 58
          ? 'width=320,height=600'
          : 'width=400,height=600';
      return printHtmlInBrowser(html, { windowFeatures });
    }

    return printSilentFast(html, {
      margins: plan.margins,
      kind: plan.kind,
      // Paper layout may be A5 (kind: document) but routing stays on receipt role.
      role: PRINT_ROLE.RECEIPT,
      pageWidthMm: plan.pageWidthMm,
      pageHeightMm: plan.pageHeightMm,
      timeoutMs: plan.timeoutMs,
      retries: plan.retries,
      imageWaitMs: plan.imageWaitMs
    });
  }
}

// Export singleton instance
export const receiptCalculationService = new ReceiptCalculationService();
export default receiptCalculationService;
